import React, { useRef, useState } from 'react';
import { Button, CircularProgress, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { useNotify, useTranslate } from 'react-admin';
import { aiGeneratorService, apiErrorMessage } from '../../services/aiGeneratorService';

export type MaterialMode = 'text' | 'url' | 'file';

export interface MaterialValue {
    mode: MaterialMode;
    text: string;
    url: string;
    /** Set after a file was extracted or a URL fetched (name / page title). */
    sourceLabel: string | null;
    sourceType: string | null;
    truncated: boolean;
}

export const emptyMaterial = (): MaterialValue => ({ mode: 'text', text: '', url: '', sourceLabel: null, sourceType: null, truncated: false });

/**
 * Source material for both generators (Tranche B2, #489): pasted text, a
 * public web page (fetched server-side with the SSRF guard) or a
 * PDF/DOCX/PPTX upload (text extracted server-side, nothing stored). All
 * three end in the same editable text field so the trainer can trim it.
 */
export const MaterialInput: React.FC<{ value: MaterialValue; onChange: (value: MaterialValue) => void; minChars?: number; optional?: boolean; disabled?: boolean }> = ({ value, onChange, minChars = 50, optional = false, disabled }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const fileInput = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);

    const setMode = (_: unknown, mode: MaterialMode | null) => {
        if (mode) onChange({ ...value, mode });
    };

    const fetchUrl = async () => {
        if (!value.url.trim()) return;
        setBusy(true);
        try {
            const result = await aiGeneratorService.fetchUrl(value.url.trim());
            onChange({ ...value, text: result.text, sourceLabel: result.title || result.url || value.url, sourceType: 'url', truncated: result.truncated });
            notify(translate('aiGenerator.material.fetched', { chars: result.characters }), { type: 'info' });
        } catch (e) {
            notify(apiErrorMessage(e, translate('aiGenerator.material.fetchError')), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const extractFile = async (file: File | undefined) => {
        if (!file) return;
        setBusy(true);
        try {
            const result = await aiGeneratorService.extractFile(file);
            onChange({ ...value, text: result.text, sourceLabel: result.filename || file.name, sourceType: result.source_type, truncated: result.truncated });
            notify(translate('aiGenerator.material.extracted', { chars: result.characters }), { type: 'info' });
        } catch (e) {
            notify(apiErrorMessage(e, translate('aiGenerator.material.extractError')), { type: 'error' });
        } finally {
            setBusy(false);
            if (fileInput.current) fileInput.current.value = '';
        }
    };

    const chars = value.text.trim().length;

    return (
        <div className="oa-aig-material">
            <div className="oa-aig-material-tabs">
                <ToggleButtonGroup size="small" exclusive value={value.mode} onChange={setMode} disabled={disabled} aria-label={translate('aiGenerator.material.mode')}>
                    <ToggleButton value="text">{translate('aiGenerator.material.text')}</ToggleButton>
                    <ToggleButton value="url">{translate('aiGenerator.material.url')}</ToggleButton>
                    <ToggleButton value="file">{translate('aiGenerator.material.file')}</ToggleButton>
                </ToggleButtonGroup>
            </div>
            {value.mode === 'url' && (
                <div className="oa-aig-form-row">
                    <TextField
                        size="small"
                        label={translate('aiGenerator.material.urlField')}
                        placeholder="https://"
                        value={value.url}
                        onChange={(e) => onChange({ ...value, url: e.target.value })}
                        disabled={disabled || busy}
                        inputProps={{ 'aria-label': translate('aiGenerator.material.urlField') }}
                    />
                    <Button variant="outlined" onClick={() => void fetchUrl()} disabled={disabled || busy || !value.url.trim()} startIcon={busy ? <CircularProgress size={16} /> : undefined}>
                        {translate('aiGenerator.material.fetch')}
                    </Button>
                </div>
            )}
            {value.mode === 'file' && (
                <div className="oa-aig-form-row">
                    <input ref={fileInput} type="file" accept=".pdf,.docx,.pptx" hidden data-testid="material-file" onChange={(e) => void extractFile(e.target.files?.[0])} />
                    <Button variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <UploadFileOutlined />} onClick={() => fileInput.current?.click()} disabled={disabled || busy}>
                        {translate('aiGenerator.material.chooseFile')}
                    </Button>
                    <span className="oa-aig-material-meta">{translate('aiGenerator.material.fileHint')}</span>
                </div>
            )}
            <TextField
                multiline
                minRows={value.mode === 'text' ? 8 : 5}
                maxRows={18}
                fullWidth
                size="small"
                label={translate('aiGenerator.material.textField')}
                value={value.text}
                onChange={(e) => onChange({ ...value, text: e.target.value })}
                disabled={disabled || busy}
                inputProps={{ 'aria-label': translate('aiGenerator.material.textField') }}
            />
            <div className="oa-aig-material-meta">
                <span>{optional ? translate('aiGenerator.material.charsOptional', { chars }) : translate('aiGenerator.material.chars', { chars, min: minChars })}</span>
                {value.sourceLabel && <span>{translate('aiGenerator.material.source', { source: value.sourceLabel })}</span>}
                {value.truncated && <span>{translate('aiGenerator.material.truncated')}</span>}
            </div>
        </div>
    );
};

export default MaterialInput;
